/* =============================================================================
 * 栀歆Talk 物理动效预设（T05）
 * -----------------------------------------------------------------------------
 * 全站唯一的动效取值来源。所有页面切换、按压、列表入场、底部弹层都必须引用
 * 这里的预设，不允许各写一套。
 *
 * 设计立场：动效不是装饰，而是"物理世界的因果暗示"。
 * 每个 spring 都由 stiffness（刚度/回弹力）、damping（阻尼/能量损耗）、
 * mass（质量/惯性）三参数描述，注释里给出物理直觉与手感目标。
 *
 * 无障碍：prefers-reduced-motion 下由 useMotionPresets() 自动降级为淡入淡出，
 * 位移与缩放全部归零，只保留透明度变化。
 * ============================================================================= */

import { useReducedMotion } from 'framer-motion';
import type { Transition, Variants } from 'framer-motion';

/** 独立页按层级前进 / 后退；主 tab 按目标在导航中的左 / 右位置切换。 */
export type PageDirection = 'forward' | 'backward' | 'tab-left' | 'tab-right';

/** 弹簧三参数：刚度 / 阻尼 / 质量 */
export interface SpringPhysics {
  /** 刚度：越大回弹越快、越"硬" */
  stiffness: number;
  /** 阻尼：越大能量损耗越快、越不震荡 */
  damping: number;
  /** 质量：越大惯性越强、启动与收尾越"重" */
  mass: number;
}

/** 逻辑画布宽度（与 PhoneFrame 的 390 x 844 对齐），用于页面位移距离 */
export const PAGE_WIDTH = 390;

/* -----------------------------------------------------------------------------
 * 1. 弹簧常量
 * -------------------------------------------------------------------------- */

/**
 * 页面切换：中等刚度 + 较高阻尼 + 接近 1 的质量。
 * 物理直觉：像一扇有一定重量的门被推开 —— 起步有惯性、到位有一次很轻的收尾，
 * 几乎不产生可见震荡（欠阻尼会让人晕，过阻尼会显得迟钝）。
 */
export const pageSpring: SpringPhysics = { stiffness: 320, damping: 34, mass: 0.9 };

/**
 * 按压反馈：高刚度 + 较低阻尼 + 小质量。
 * 物理直觉：手指按下一块轻薄按键 —— 形变立刻发生、松手后迅速弹回原状，
 * 带一点点过冲的"Q 弹"手感，但不拖泥带水。
 */
export const tapSpring: SpringPhysics = { stiffness: 480, damping: 26, mass: 0.55 };

/**
 * 底部弹层：较低刚度 + 中等阻尼 + 偏大质量。
 * 物理直觉：一块较重的抽屉沿导轨上滑 —— 起步沉、行程末端被阻尼稳稳收住，
 * 营造"重物落位"的确定感。
 */
export const sheetSpring: SpringPhysics = { stiffness: 260, damping: 30, mass: 1.1 };

/** 列表逐项入场：与页面弹簧同族，保证同屏内动效语言一致 */
export const listSpring: SpringPhysics = { stiffness: 300, damping: 30, mass: 0.8 };

/* -----------------------------------------------------------------------------
 * 2. 页面切换预设
 * -------------------------------------------------------------------------- */

/**
 * 页面切换（前进 / 后退方向不同）。
 * - 前进：新页面从右侧推入，旧页面向左退让（视差），符合"向右走进入下一层"的空间模型；
 * - 后退：父页面从左侧 28% 视差位置恢复，子页面完整向右滑出。
 * - 主 tab：新页面从目标所在的左 / 右侧完整滑入，旧页面完整滑出至相反侧。
 * 用法：<AnimatePresence custom={direction}><motion.div variants={pageTransition} .../></AnimatePresence>
 */
export const pageTransition: Variants = {
  enter: (direction: PageDirection) => {
    if (direction === 'tab-left' || direction === 'tab-right') {
      return {
        x: direction === 'tab-left' ? -PAGE_WIDTH : PAGE_WIDTH,
        opacity: 1,
        scale: 1,
        zIndex: 2,
      };
    }
    return {
      x: direction === 'forward' ? PAGE_WIDTH : -PAGE_WIDTH * 0.28,
      opacity: 0.4,
      scale: 0.98,
      zIndex: direction === 'forward' ? 2 : 1,
    };
  },
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: { type: 'spring', ...pageSpring } satisfies Transition,
  },
  exit: (direction: PageDirection) => {
    if (direction === 'tab-left' || direction === 'tab-right') {
      return {
        x: direction === 'tab-left' ? PAGE_WIDTH : -PAGE_WIDTH,
        opacity: 1,
        scale: 1,
        zIndex: 1,
        transition: { type: 'spring', ...pageSpring } satisfies Transition,
      };
    }
    return {
      // Push leaves the parent behind; pop takes the feature completely offscreen.
      x: direction === 'forward' ? -PAGE_WIDTH * 0.28 : PAGE_WIDTH,
      opacity: 0,
      scale: 0.98,
      zIndex: direction === 'forward' ? 1 : 2,
      transition: { type: 'spring', ...pageSpring } satisfies Transition,
    };
  },
};

/* -----------------------------------------------------------------------------
 * 3. 按压 / 列表 / 弹层预设
 * -------------------------------------------------------------------------- */

/** 按压缩放回弹：whileTap scale 0.96，松手按 tapSpring 弹回 */
export const tap = {
  whileTap: { scale: 0.96 },
  transition: { type: 'spring', ...tapSpring } satisfies Transition,
} as const;

/** 列表逐项入场：容器控制 stagger，子项从下方 12px 淡入并带轻微惯性 */
export const listStagger: {
  container: Variants;
  item: Variants;
} = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05, delayChildren: 0.02 },
    },
  },
  item: {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', ...listSpring } satisfies Transition,
    },
  },
};

/** 底部弹层上滑：y 从 100% 到 0，按 sheetSpring 落位 */
export const sheet: Variants = {
  hidden: { y: '100%' },
  visible: {
    y: 0,
    transition: { type: 'spring', ...sheetSpring } satisfies Transition,
  },
};

/** 弹层遮罩淡入 */
export const sheetBackdrop: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.18, ease: [0.2, 0, 0, 1] } },
};

/* -----------------------------------------------------------------------------
 * 4. prefers-reduced-motion 降级
 * -------------------------------------------------------------------------- */

/** 降级页面的淡入淡出（无位移、无缩放） */
export const fadeTransition: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.18, ease: [0.2, 0, 0, 1] } },
  exit: { opacity: 0, transition: { duration: 0.14, ease: [0.2, 0, 0, 1] } },
};

/** 降级后的动效预设集合（结构与正常预设一致，便于无感替换） */
export interface MotionPresets {
  reduced: boolean;
  pageTransition: Variants;
  tap: { whileTap: { scale: number }; transition: Transition };
  listStagger: { container: Variants; item: Variants };
  sheet: Variants;
  sheetBackdrop: Variants;
}

/**
 * 按系统偏好返回可用的动效预设。
 * 开启 prefers-reduced-motion 时，位移/缩放/交错全部关闭，只保留淡入淡出。
 */
export function useMotionPresets(): MotionPresets {
  const reduced = useReducedMotion() ?? false;

  if (reduced) {
    return {
      reduced: true,
      pageTransition: fadeTransition,
      tap: { whileTap: { scale: 1 }, transition: { duration: 0 } },
      listStagger: {
        container: { hidden: {}, visible: {} },
        item: {
          hidden: { opacity: 0 },
          visible: { opacity: 1, transition: { duration: 0.18 } },
        },
      },
      sheet: {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.18 } },
      },
      sheetBackdrop: sheetBackdrop,
    };
  }

  return {
    reduced: false,
    pageTransition,
    tap,
    listStagger,
    sheet,
    sheetBackdrop,
  };
}
